/**
 * Customer Quote Request Page
 * Allows customers to select a service and get an instant pricing quote
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import CustomerPricingQuoteForm from '@/components/quote/CustomerPricingQuoteForm';
import apiService from '@/services/api';

const SERVICES_BY_CATEGORY = {
  Electrical: [
    { id: 134, name: 'CCTV Installation' },
    { id: 135, name: 'Consumer Unit Replacement' },
    { id: 136, name: 'EICR Inspection' },
    { id: 137, name: 'Electrical Installation' },
  ],
  Landscaping: [
    { id: 138, name: 'Decking' },
    { id: 139, name: 'Fencing' },
    { id: 140, name: 'Garden Clearance' },
    { id: 141, name: 'Garden Maintenance' },
    { id: 142, name: 'Hedge Cutting' },
    { id: 143, name: 'Landscaping' },
    { id: 144, name: 'Pressure Washing' },
    { id: 145, name: 'Tree Surgery' },
    { id: 146, name: 'Turfing' },
  ],
};

interface QuoteData {
  service_id: number;
  service_name: string;
  selected_options: string[];
  estimated_cost: number;
  estimate_type: string;
  confidence_score: number;
}

export default function CustomerQuoteRequestPage() {
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [submittedQuote, setSubmittedQuote] = useState<QuoteData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleQuoteSubmit = async (quoteData: QuoteData) => {
    try {
      setSubmitting(true);
      setError(null);

      // Create a booking/request with the quote
      const response = await apiService.request(
        '/quotes',
        'POST',
        {
          service_id: quoteData.service_id,
          service_name: quoteData.service_name,
          estimated_cost: quoteData.estimated_cost,
          estimate_type: quoteData.estimate_type,
          selected_options: quoteData.selected_options,
          confidence_score: quoteData.confidence_score,
          status: 'pending_provider_response',
        }
      );

      if (response.success) {
        setSubmittedQuote(quoteData);
      } else {
        setError(response.error || 'Failed to submit quote');
      }
    } catch (err) {
      console.error('Error submitting quote:', err);
      setError('Failed to submit quote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedService(null);
    setSubmittedQuote(null);
    setError(null);
  };

  if (submittedQuote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-8">
              <div className="text-center space-y-4">
                <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
                <h2 className="text-2xl font-bold text-gray-900">Quote Submitted Successfully!</h2>
                <p className="text-gray-700">
                  Your quote for <strong>{submittedQuote.service_name}</strong> has been submitted.
                </p>
                <p className="text-3xl font-bold text-green-600 py-4">
                  £{submittedQuote.estimated_cost.toFixed(2)}
                </p>
                <div className="grid grid-cols-2 gap-4 py-4">
                  <div className="bg-white rounded p-3 border border-green-200">
                    <p className="text-xs text-gray-600 font-medium">Estimate Type</p>
                    <p className="text-sm font-bold text-gray-900 mt-1">
                      {submittedQuote.estimate_type === 'FIXED' && 'Fixed Price'}
                      {submittedQuote.estimate_type === 'RANGE' && 'Price Range'}
                      {submittedQuote.estimate_type === 'ASSESSMENT_REQUIRED' && 'Assessment Needed'}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 border border-green-200">
                    <p className="text-xs text-gray-600 font-medium">Confidence Score</p>
                    <p className="text-sm font-bold text-gray-900 mt-1">
                      {submittedQuote.confidence_score}%
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 py-4">
                  A provider will review your quote and contact you within 24 hours with details.
                </p>
                <Button onClick={resetForm} className="w-full bg-green-600 hover:bg-green-700">
                  Get Another Quote <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Get an Instant Quote</h1>
          <p className="text-xl text-gray-600">
            Answer a few questions and get your pricing estimate in seconds
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!selectedService ? (
          <div className="space-y-6">
            {/* Service Category Grid */}
            {Object.entries(SERVICES_BY_CATEGORY).map(([category, services]) => (
              <Card key={category}>
                <CardHeader>
                  <CardTitle>{category} Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {services.map((service) => (
                      <Button
                        key={service.id}
                        onClick={() => setSelectedService(service.id)}
                        variant="outline"
                        className="justify-start text-left h-auto py-3 px-4 hover:bg-blue-50 hover:border-blue-300"
                      >
                        <div>
                          <p className="font-medium">{service.name}</p>
                          <p className="text-xs text-gray-500 mt-1">Click to get quote</p>
                        </div>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <Button
              onClick={() => setSelectedService(null)}
              variant="outline"
              className="mb-4"
            >
              ← Change Service
            </Button>
            <CustomerPricingQuoteForm
              serviceId={selectedService}
              onQuoteSubmit={handleQuoteSubmit}
            />
          </div>
        )}

        {/* Info Cards */}
        {!selectedService && (
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg">How It Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <p className="font-medium">Select a Service</p>
                  <p className="text-sm text-gray-600">Choose from our electrical and landscaping services</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <p className="font-medium">Answer Questions</p>
                  <p className="text-sm text-gray-600">Tell us about your specific needs and requirements</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <p className="font-medium">Get Quote Instantly</p>
                  <p className="text-sm text-gray-600">Receive your estimated price immediately</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                  4
                </div>
                <div>
                  <p className="font-medium">Provider Response</p>
                  <p className="text-sm text-gray-600">A provider will contact you within 24 hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
